import { Controller } from '@hotwired/stimulus'
import { Calendar } from 'vanilla-calendar-pro'
import { getDateString } from 'vanilla-calendar-pro/utils'

// Popup date picker (vanilla-calendar-pro) for a text input holding an MM/DD/YYYY date.
// Dispatches `datepicker:change` whenever the date is picked, cleared, or typed. Typed dates are
// rewritten to MM/DD/YYYY (2-digit years land within 20 years of today); unparseable text reverts.
const DATE_MIN = '1970-01-01'
const DATE_MAX = '2470-12-31'
const NEARBY_YEARS = 20
const CLEAR_BUTTON = '<div class="vc-footer"><button type="button" class="btn btn-link btn-sm p-0" data-datepicker-clear>Clear</button></div>'

export default class extends Controller {
  static values = { clearButton: Boolean }

  connect() {
    const selected = this.isoFromInput()
    this.lastValue = selected ? this.formatted(selected) : ''

    this.calendar = new Calendar(this.element, {
      inputMode: true,
      positionToInput: 'auto',
      firstWeekday: 0,
      selectedWeekends: [],
      selectedDates: selected ? [selected] : [],
      enableJumpToSelectedDate: true,
      enableDateToggle: false,
      onInit: (self) => this.decorate(self),
      onShow: (self) => this.syncFromInput(self),
      onChangeToInput: (self) => this.pick(self.context.selectedDates[0])
    })
    this.calendar.init()

    this.element.addEventListener('change', this.onTyped)
  }

  disconnect() {
    this.element.removeEventListener('change', this.onTyped)
    this.calendar?.destroy()
    this.calendar = null
  }

  setStartDate(date) {
    this.calendar.set({ dateMin: date ? getDateString(date) : DATE_MIN }, { dates: false, month: false, year: false })
  }

  setEndDate(date) {
    this.calendar.set({ dateMax: date ? getDateString(date) : DATE_MAX }, { dates: false, month: false, year: false })
  }

  decorate(self) {
    const popup = self.context.mainElement

    popup.addEventListener('mousedown', (event) => event.preventDefault())
    popup.addEventListener('click', (event) => event.stopPropagation())

    if (this.clearButtonValue) {
      self.layouts.default += CLEAR_BUTTON
      self.update({ dates: false, month: false, year: false })
      popup.addEventListener('click', (event) => {
        if (!event.target.closest('[data-datepicker-clear]')) return

        self.set({ selectedDates: [] }, { dates: true, month: false, year: false })
        this.pick(null)
      })
    }
  }

  syncFromInput(self) {
    const selected = this.isoFromInput()
    if (selected === (self.context.selectedDates[0] ?? null)) return

    self.set({ selectedDates: selected ? [selected] : [] })
  }

  pick(isoDate) {
    this.element.value = isoDate ? this.formatted(isoDate) : ''
    this.lastValue = this.element.value
    this.calendar.hide()
    this.dispatchChange()
  }

  onTyped = () => {
    const selected = this.isoFromInput()
    if (this.element.value.trim() && !selected) {
      this.element.value = this.lastValue
      return
    }

    this.element.value = selected ? this.formatted(selected) : ''
    this.lastValue = this.element.value
    this.calendar.set({ selectedDates: selected ? [selected] : [] })
    this.dispatchChange()
  }

  dispatchChange() {
    this.element.dispatchEvent(new CustomEvent('datepicker:change', { bubbles: true }))
  }

  isoFromInput() {
    const match = this.element.value.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/)
    if (!match) return null

    const [, month, day] = match.map(Number)
    const year = match[3].length === 2 ? this.nearbyYear(Number(match[3])) : Number(match[3])
    if (year < 1900) return null

    const date = new Date(year, month - 1, day)
    if (date.getMonth() !== month - 1 || date.getDate() !== day) return null

    return getDateString(date)
  }

  nearbyYear(twoDigitYear) {
    const thisYear = new Date().getFullYear()
    const year = thisYear - (thisYear % 100) + twoDigitYear
    return year > thisYear + NEARBY_YEARS ? year - 100 : year
  }

  formatted(isoDate) {
    const [year, month, day] = isoDate.split('-')
    return `${month}/${day}/${year}`
  }
}
